import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of, shareReplay, timer } from 'rxjs';
import { tap, catchError, switchMap, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = `${environment.apiUrl}/api/users`;
  private currentUserSubject = new BehaviorSubject<any>(null);
  public currentUser = this.currentUserSubject.asObservable();
  private isLoggedInSubject = new BehaviorSubject<boolean>(false);
  public isLoggedIn$ = this.isLoggedInSubject.asObservable();
  private currentUserRequest$: Observable<any> | null = null;
  /** Bumped on login/clear so stale in-flight /me responses cannot wipe a new session. */
  private authSessionEpoch = 0;
  private lastLoginAt = 0;

  constructor(private http: HttpClient, private router: Router) {
    // Removed: this.getCurrentUser().subscribe();
  }

  public get currentUserValue(): any {
    return this.currentUserSubject.value;
  }

  login(email: string, password: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/login`, { email, password }).pipe(
      tap(response => {
        if (response.user) {
          this.authSessionEpoch += 1;
          this.lastLoginAt = Date.now();
          this.currentUserSubject.next(response.user);
          this.isLoggedInSubject.next(true);
          this.currentUserRequest$ = null;
        } else {
          this.currentUserSubject.next(null);
          this.isLoggedInSubject.next(false);
        }
      }),
      map(response => response.user)
    );
  }

  clearSession(): void {
    this.authSessionEpoch += 1;
    this.currentUserSubject.next(null);
    this.isLoggedInSubject.next(false);
    this.currentUserRequest$ = null;
    localStorage.clear();
    sessionStorage.clear();
    this.router.navigate(['/login']);
  }

  logout(): void {
    this.http.post(`${this.apiUrl}/logout`, {}).subscribe({
      next: () => {
        this.clearSession();
      },
      error: () => {
        this.clearSession();
      }
    });
  }

  getLastLoginAt(): number {
    return this.lastLoginAt;
  }

  getCurrentUser(): Observable<any> {
    if (this.currentUserValue !== null) {
      return of(this.currentUserValue);
    }
    if (this.currentUserRequest$) {
      return this.currentUserRequest$;
    }
    const requestEpoch = this.authSessionEpoch;
    this.currentUserRequest$ = this.http.get<any>(`${this.apiUrl}/me`).pipe(
      tap(user => {
        if (requestEpoch === this.authSessionEpoch) {
          this.currentUserSubject.next(user);
          this.isLoggedInSubject.next(!!user);
        }
        this.currentUserRequest$ = null;
      }),
      catchError(() => {
        this.currentUserRequest$ = null;
        if (requestEpoch !== this.authSessionEpoch) {
          return of(this.currentUserValue);
        }
        if (!this.currentUserValue) {
          this.currentUserSubject.next(null);
          this.isLoggedInSubject.next(false);
        }
        return of(this.currentUserValue);
      }),
      shareReplay(1)
    );
    return this.currentUserRequest$;
  }

  hasRole(role: string): boolean {
    const currentUser = this.currentUserValue;
    if (currentUser && currentUser.roles) {
      return currentUser.roles.includes(role);
    }
    return false;
  }

  hasRoleAry(roles: string | string[]): boolean {
    if (typeof roles === 'string') {
      roles = [roles];
    }
    const currentUser = this.currentUserValue;
    return currentUser && currentUser.roles && roles.some((role: string) => currentUser.roles.includes(role));
  }

  isLoggedIn(): boolean {
    return !!this.currentUserValue;
  }
}
